// src/hooks/useApi.js

import { useCallback, useContext, useMemo } from 'react'; // <-- Import useMemo
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { BASE_URL } from '../config/api';

const useApi = () => {
  const { token, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const request = useCallback(async (endpoint, options = {}) => {
    const defaultHeaders = {
      'Accept': 'application/json',
    };

    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    let url = `${BASE_URL}/${endpoint}`;
    if (options.method === 'GET' && options.queryParams) {
      const queryString = new URLSearchParams(options.queryParams).toString();
      if (queryString) {
        url = `${url}?${queryString}`;
      }
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        console.warn(`useApi: API returned 401 Unauthorized for ${endpoint}. Logging out user.`);
        console.warn("Your session has expired or is invalid. Please log in again.");
        logout();
        navigate('/login');
        throw new Error('Authentication failed or token expired.');
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'An unexpected error occurred.' };
        }
        // Create an error object that includes the full response data
        const error = new Error(errorData.message || `Request to ${endpoint} failed with status: ${response.status}`);
        error.data = errorData; // Attach the full payload
        throw error; // Throw the enhanced error object
      }

      if (response.status === 204) {
        return {};
      }
      
      return await response.json();

    } catch (error) {
      console.error(`useApi: API request to ${endpoint} failed:`, error);
      throw error;
    }
  }, [token, logout, navigate]);

  // Define memoized functions first
  const get = useCallback((endpoint, options) => request(endpoint, { method: 'GET', ...options }), [request]);
  
  const post = useCallback((endpoint, body, options) => {
    const isFormData = body instanceof FormData;
    return request(endpoint, {
      method: 'POST',
      body: isFormData ? body : JSON.stringify(body),
      ...options
    });
  }, [request]);

  const put = useCallback((endpoint, body, options) => {
    const isFormData = body instanceof FormData;
    return request(endpoint, {
      method: 'PUT',
      body: isFormData ? body : JSON.stringify(body),
      ...options
    });
  }, [request]);

  const patch = useCallback((endpoint, body, options) => {
    const isFormData = body instanceof FormData;
    return request(endpoint, {
      method: 'PATCH',
      body: isFormData ? body : JSON.stringify(body),
      ...options
    });
  }, [request]);

  const del = useCallback((endpoint, options) => request(endpoint, { method: 'DELETE', ...options }), [request]);

  // **THE FIX**: Memoize the returned object to ensure its reference is stable
  const apiMethods = useMemo(() => ({
    get,
    post,
    put,
    delete: del, patch
  }), [get, post, put, del]);

  return apiMethods;
};

export default useApi;
